import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface AddModuleModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (tabName: string, sectionTitle: string) => void;
  editMode?: boolean; // Düzenleme modu
  initialTabName?: string; // Düzenlenecek sekme adı
  initialSectionTitle?: string; // Düzenlenecek bölüm başlığı
  onUpdate?: (tabName: string, sectionTitle: string) => void; // Güncelleme fonksiyonu
}

const AddModuleModal: React.FC<AddModuleModalProps> = ({ 
  open, 
  onClose, 
  onAdd, 
  editMode = false,
  initialTabName = '',
  initialSectionTitle = '',
  onUpdate
}) => {
  const { t } = useTranslation('salesSystem');
  const [tabName, setTabName] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Modal açıldığında alanları ayarla
    if (open) {
      if (editMode) {
        // Düzenleme modunda mevcut değerleri yükle
        setTabName(initialTabName);
        setSectionTitle(initialSectionTitle);
      } else {
        // Yeni ekleme modunda alanları temizle
        setTabName('');
        setSectionTitle('');
      }
      setError('');
    }
  }, [open, editMode, initialTabName, initialSectionTitle]);

  const handleSubmit = () => {
    if (!tabName.trim()) {
      setError(t('ecommerceHomeSettings.moduleModal.tabNameRequired'));
      return;
    }
    setError('');
    const normalizedTabName = tabName.trim();
    const normalizedSectionTitle = sectionTitle.trim();
    
    if (editMode && onUpdate) {
      // Düzenleme modu
      onUpdate(normalizedTabName, normalizedSectionTitle);
    } else {
      // Yeni ekleme modu
      onAdd(normalizedTabName, normalizedSectionTitle);
    }
    
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editMode
          ? t('ecommerceHomeSettings.moduleModal.editTitle')
          : t('ecommerceHomeSettings.moduleModal.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            id="tabName"
            label={t('ecommerceHomeSettings.moduleModal.tabNameLabel')}
            type="text"
            fullWidth
            variant="outlined"
            value={tabName}
            onChange={(e) => setTabName(e.target.value)}
            helperText={t('ecommerceHomeSettings.moduleModal.tabNameHelper')}
          />
          <TextField
            margin="dense"
            id="sectionTitle"
            label={t('ecommerceHomeSettings.moduleModal.sectionTitleLabel')}
            type="text"
            fullWidth
            variant="outlined"
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            helperText={t('ecommerceHomeSettings.moduleModal.sectionTitleHelper')}
          />
          {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceHomeSettings.actions.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained">
          {editMode
            ? t('ecommerceHomeSettings.moduleModal.update')
            : t('ecommerceHomeSettings.moduleModal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddModuleModal; 